<template>
  <v-container fluid class="vacuum-view pa-0 pa-md-2">
    <v-row dense>
      <v-col cols="12" lg="7" xl="8">
        <v-card color="grey darken-4" dark>
          <v-card-title class="d-flex align-center flex-wrap text-subtitle-1 text-sm-h6 pa-2 pa-sm-4">
            <v-icon left>mdi-robot-vacuum</v-icon>
            Schnuffel
            <v-chip
              v-if="status"
              :color="stateColor"
              x-small
              class="ml-2"
              text-color="white"
            >{{ stateLabel }}</v-chip>
            <v-spacer />
            <v-tooltip top open-delay="200">
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  :loading="restarting"
                  icon
                  small
                  color="warning"
                  class="mr-1"
                  v-on="on"
                  @click="confirmRestart = true"
                >
                  <v-icon small>mdi-restart</v-icon>
                </v-btn>
              </template>
              <span>Backend neu starten</span>
            </v-tooltip>
            <v-icon :color="batteryColor" small>{{ batteryIcon }}</v-icon>
            <span class="text-body-2 ml-1">{{ status ? status.status.battery + ' %' : '—' }}</span>
          </v-card-title>
          <v-card-text class="pa-2 pa-md-3">
            <VacuumMap
              :map-buffer="mapBuffer"
              :rooms="rooms"
              :error="mapError"
              :mode="mapMode"
              :goto-target="gotoTarget"
              :devices="placeableDevices"
              :routines="routines"
              :placing-name="placingName"
              @select-segment="onMapSegment"
              @pin-and-go="onPinAndGo"
              @pin-and-go-invalid="snack = { show: true, msg: 'Auf eine Wand kann nicht gefahren werden', color: 'warning' }"
              @robot-click="statusDialog = true"
              @place-device="onPlaceDevice"
              @device-click="toggleDevice"
              @routine-click="toggleRoutine"
            />
          </v-card-text>
          <v-divider />
          <v-card-actions class="flex-wrap justify-center pa-2">
            <v-tooltip top open-delay="200">
              <template #activator="{ on, attrs }">
                <v-btn v-bind="attrs" :loading="busy.start" color="primary" :small="compactBtn" class="ma-1" v-on="on" @click="act('start')">
                  <v-icon :left="!iconOnlyBtn" :small="compactBtn">mdi-play</v-icon>
                  <span v-if="!iconOnlyBtn">Komplett</span>
                </v-btn>
              </template>
              <span>Komplette Reinigung aller Räume starten</span>
            </v-tooltip>
            <v-tooltip top open-delay="200">
              <template #activator="{ on, attrs }">
                <v-btn v-bind="attrs" :loading="busy.pause" :small="compactBtn" class="ma-1" v-on="on" @click="act('pause')">
                  <v-icon :left="!iconOnlyBtn" :small="compactBtn">mdi-pause</v-icon>
                  <span v-if="!iconOnlyBtn">Pause</span>
                </v-btn>
              </template>
              <span>Reinigung pausieren — kann fortgesetzt werden</span>
            </v-tooltip>
            <v-tooltip top open-delay="200">
              <template #activator="{ on, attrs }">
                <v-btn v-bind="attrs" :loading="busy.stop" :small="compactBtn" class="ma-1" v-on="on" @click="act('stop')">
                  <v-icon :left="!iconOnlyBtn" :small="compactBtn">mdi-stop</v-icon>
                  <span v-if="!iconOnlyBtn">Stopp</span>
                </v-btn>
              </template>
              <span>Reinigung komplett abbrechen</span>
            </v-tooltip>
            <v-tooltip top open-delay="200">
              <template #activator="{ on, attrs }">
                <v-btn v-bind="attrs" :loading="busy.dock" color="green darken-2" :small="compactBtn" class="ma-1" v-on="on" @click="act('dock')">
                  <v-icon :left="!iconOnlyBtn" :small="compactBtn">mdi-home-import-outline</v-icon>
                  <span v-if="!iconOnlyBtn">Zum Dock</span>
                </v-btn>
              </template>
              <span>Zurück zur Ladestation schicken</span>
            </v-tooltip>
            <v-tooltip top open-delay="200">
              <template #activator="{ on, attrs }">
                <v-btn v-bind="attrs" :loading="busy.find" icon :small="compactBtn" class="ma-1" v-on="on" @click="act('find')">
                  <v-icon :small="compactBtn">mdi-bullhorn</v-icon>
                </v-btn>
              </template>
              <span>Roboter piepen lassen, um ihn zu finden</span>
            </v-tooltip>
            <v-divider v-if="!iconOnlyBtn" vertical class="mx-2" />
            <v-tooltip top open-delay="200">
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  :color="mapMode === 'pin-and-go' ? 'amber darken-2' : ''"
                  :dark="mapMode === 'pin-and-go'"
                  :small="compactBtn"
                  class="ma-1"
                  v-on="on"
                  @click="togglePinAndGo"
                >
                  <v-icon :left="!iconOnlyBtn" :small="compactBtn">mdi-map-marker-radius</v-icon>
                  <span v-if="!iconOnlyBtn">{{ mapMode === 'pin-and-go' ? 'Pin & Go aktiv' : 'Pin & Go' }}</span>
                </v-btn>
              </template>
              <span>Punkt auf der Karte antippen — Roboter fährt direkt dorthin</span>
            </v-tooltip>
            <v-tooltip top open-delay="200">
              <template #activator="{ on, attrs }">
                <v-btn v-bind="attrs" :small="compactBtn" class="ma-1" v-on="on" @click="deviceDialog = true">
                  <v-icon :left="!iconOnlyBtn" :small="compactBtn">mdi-lightbulb-group</v-icon>
                  <span v-if="!iconOnlyBtn">Geräte</span>
                </v-btn>
              </template>
              <span>Lampen und Steckdosen auf der Karte platzieren und schalten</span>
            </v-tooltip>
            <v-tooltip top open-delay="200">
              <template #activator="{ on, attrs }">
                <v-btn v-bind="attrs" :small="compactBtn" class="ma-1" v-on="on" @click="rcDialog = true">
                  <v-icon :left="!iconOnlyBtn" :small="compactBtn">mdi-gamepad-variant</v-icon>
                  <span v-if="!iconOnlyBtn">RC</span>
                </v-btn>
              </template>
              <span>Fernsteuerung per Joystick / Pfeiltasten öffnen</span>
            </v-tooltip>
            <v-tooltip top open-delay="200">
              <template #activator="{ on, attrs }">
                <v-btn v-bind="attrs" :loading="busy.wash" :small="compactBtn" class="ma-1" v-on="on" @click="act('wash')">
                  <v-icon :left="!iconOnlyBtn" :small="compactBtn">mdi-washing-machine</v-icon>
                  <span v-if="!iconOnlyBtn">Wash</span>
                </v-btn>
              </template>
              <span>Wischmopp am Dock waschen</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12" lg="5" xl="4">
        <v-card color="grey darken-4" dark>
          <v-card-title>
            <v-icon left>mdi-floor-plan</v-icon>Räume
            <v-spacer />
            <v-btn icon small @click="refresh"><v-icon small>mdi-refresh</v-icon></v-btn>
          </v-card-title>
          <v-card-subtitle>Räume antippen, um sie auszuwählen — dann unten die Reinigung starten.</v-card-subtitle>
          <v-card-text>
            <v-row dense>
              <v-col
                v-for="room in rooms"
                :key="room.segment_id"
                cols="6"
              >
                <v-card
                  :color="roomColor(room)"
                  class="room-tile"
                  :class="{ 'room-selected': isSelected(room) }"
                  hover
                  @click="toggleRoom(room)"
                >
                  <v-icon v-if="isSelected(room)" class="room-check" small>mdi-check-circle</v-icon>
                  <v-card-text class="text-center white--text pa-2 pa-sm-4">
                    <div class="text-subtitle-1 text-sm-h6">{{ room.name }}</div>
                    <div class="caption">#{{ room.segment_id }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col v-if="!rooms.length" cols="12">
                <div class="text-center grey--text py-4">Keine Räume geladen.</div>
              </v-col>
            </v-row>

            <div v-if="rooms.length" class="d-flex align-center mt-2">
              <v-btn text small :disabled="!selectedSegments.length" @click="clearSelection">
                Auswahl aufheben
              </v-btn>
              <v-spacer />
              <v-btn color="primary" small :disabled="!selectedSegments.length" @click="startSelection">
                <v-icon left small>mdi-broom</v-icon>
                Reinigung starten<span v-if="selectedSegments.length">&nbsp;({{ selectedSegments.length }})</span>
              </v-btn>
            </div>

            <v-alert v-if="errorText" type="error" class="mt-3 mb-0" dense>{{ errorText }}</v-alert>
          </v-card-text>
        </v-card>

        <CleanRecordsList
          class="mt-3"
          :records="records"
          :summary="recordsSummary"
          @refresh="pollRecords"
        />
      </v-col>
    </v-row>

    <CleanRoomDialog
      v-model="dialog"
      :rooms="selectedRooms"
      @started="onStarted"
      @add-room="addRoomFromMap"
      @remove-room="removeRoom"
    />
    <RobotStatusDialog v-model="statusDialog" :status="status" />
    <RcDialog v-model="rcDialog" :status="status" />
    <DevicePlaceDialog
      v-model="deviceDialog"
      :devices="placeableDevices"
      :routines="routines"
      @place="startPlacing"
      @toggle="setDeviceOn"
      @remove="removeDeviceFromMap"
      @place-routine="startPlacingRoutine"
      @toggle-routine="setRoutineActive"
      @remove-routine="removeRoutineFromMap"
    />

    <!-- Backend-Neustart bestätigen -->
    <v-dialog v-model="confirmRestart" max-width="420">
      <v-card>
        <v-card-title>Backend neu starten?</v-card-title>
        <v-card-text>
          Der SmartHaus-Dienst wird neu gestartet. Für ein paar Sekunden sind keine
          Geräte-, Raum- und Strom-Aktionen verfügbar.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmRestart = false">Abbrechen</v-btn>
          <v-btn color="warning" text @click="restart">Neu starten</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" timeout="3000" bottom>
      {{ snack.msg }}
    </v-snackbar>
  </v-container>
</template>

<script>
import VacuumMap from '@/components/Vacuum/VacuumMap'
import CleanRoomDialog from '@/components/Vacuum/CleanRoomDialog'
import CleanRecordsList from '@/components/Vacuum/CleanRecordsList'
import RobotStatusDialog from '@/components/Vacuum/RobotStatusDialog'
import RcDialog from '@/components/Vacuum/RcDialog'
import DevicePlaceDialog from '@/components/Vacuum/DevicePlaceDialog'
import { vacuumApi, STATE_LABELS } from '@/services/vacuum'
import { devicesApi } from '@/services/devices'
import { smarthausApi } from '@/services/smarthaus'
import { segmentColor } from '@/services/rrMap'

const POLL_STATUS_MS = 5000
const POLL_MAP_MS = 30000
const POLL_RECORDS_MS = 60000
const POLL_DEVICES_MS = 10000

export default {
  name: 'Vacuum',
  components: { VacuumMap, CleanRoomDialog, CleanRecordsList, RobotStatusDialog, RcDialog, DevicePlaceDialog },
  data: () => ({
    status: null,
    rooms: [],
    mapBuffer: null,
    mapError: '',
    errorText: '',
    dialog: false,
    statusDialog: false,
    rcDialog: false,
    deviceDialog: false,
    devices: [],
    routines: [],
    placingDevice: null,
    placingRoutine: null,
    deviceTimer: null,
    selectedSegments: [],
    addingFromMap: false,
    statusTimer: null,
    mapTimer: null,
    recordsTimer: null,
    busy: { start: false, pause: false, stop: false, dock: false, find: false, wash: false },
    restarting: false,
    confirmRestart: false,
    snack: { show: false, msg: '', color: 'success' },
    mapMode: 'select',
    gotoTarget: null,
    records: [],
    recordsSummary: null,
  }),
  computed: {
    selectedRooms() {
      return this.rooms.filter(r => this.selectedSegments.includes(r.segment_id))
    },
    placingName() {
      if (this.placingDevice) return this.placingDevice.display_name
      if (this.placingRoutine) return `Trigger „${this.placingRoutine.name}“`
      return ''
    },
    placeableDevices() {
      // Auf der Karte machen nur Lampen und Steckdosen Sinn — der Sauger fährt selbst herum.
      return this.devices.filter(d => d.category === 'lamp' || d.category === 'plug')
    },
    compactBtn() {
      // sm und kleiner = small-Buttons, damit mehr in eine Reihe passt
      return this.$vuetify.breakpoint.smAndDown
    },
    iconOnlyBtn() {
      // xs: nur Icons, sonst gibt es ohnehin keinen Platz für mehrere Reihen Text
      return this.$vuetify.breakpoint.xsOnly
    },
    stateLabel() {
      const s = this.status && this.status.status.state
      return STATE_LABELS[s] || ('State ' + s)
    },
    stateColor() {
      const s = this.status && this.status.status.state
      if (s === 5 || s === 17 || s === 18 || s === 11) return 'primary'
      if (s === 6 || s === 15 || s === 16) return 'orange darken-2'
      if (s === 8 || s === 100) return 'green darken-2'
      if (s === 10) return 'amber darken-3'
      if (s === 12 || s === 9) return 'red darken-2'
      return 'grey darken-1'
    },
    batteryIcon() {
      const b = this.status ? this.status.status.battery : 0
      if (b >= 95) return 'mdi-battery'
      if (b >= 80) return 'mdi-battery-80'
      if (b >= 60) return 'mdi-battery-60'
      if (b >= 40) return 'mdi-battery-40'
      if (b >= 20) return 'mdi-battery-20'
      return 'mdi-battery-alert'
    },
    batteryColor() {
      const b = this.status ? this.status.status.battery : 0
      if (b < 20) return 'red'
      if (b < 40) return 'orange'
      return 'green lighten-1'
    },
  },
  mounted() {
    this.refresh()
    this.statusTimer = setInterval(this.pollStatus, POLL_STATUS_MS)
    this.mapTimer = setInterval(this.pollMap, POLL_MAP_MS)
    this.recordsTimer = setInterval(this.pollRecords, POLL_RECORDS_MS)
    this.deviceTimer = setInterval(this.pollDevices, POLL_DEVICES_MS)
  },
  beforeDestroy() {
    clearInterval(this.statusTimer)
    clearInterval(this.mapTimer)
    clearInterval(this.recordsTimer)
    clearInterval(this.deviceTimer)
  },
  methods: {
    async restart() {
      this.confirmRestart = false
      this.restarting = true
      try {
        await smarthausApi.systemRestart()
        this.snack = { show: true, msg: 'Neustart angestoßen — das Backend ist gleich wieder da.', color: 'success' }
      } catch (e) {
        console.error('[Vacuum] restart', e)
        this.snack = { show: true, msg: 'Neustart fehlgeschlagen: ' + e.message, color: 'error' }
      } finally {
        this.restarting = false
      }
    },
    async refresh() {
      await Promise.all([this.pollStatus(), this.pollRooms(), this.pollMap(), this.pollRecords(), this.pollDevices()])
    },
    async pollDevices() {
      try {
        const r = await devicesApi.list()
        this.devices = r.devices || []
      } catch (e) {
        // stiller Fehler — Geräte-Marker sind nicht kritisch
      }
      try {
        const r = await smarthausApi.routines()
        this.routines = r.routines || []
      } catch (e) {
        // stiller Fehler — Trigger-Marker sind nicht kritisch
      }
    },
    async pollStatus() {
      try {
        this.status = await vacuumApi.status()
        this.errorText = ''
      } catch (e) {
        this.errorText = e.message
      }
    },
    async pollRooms() {
      try {
        const r = await vacuumApi.rooms()
        this.rooms = r.rooms || []
      } catch (e) {
        this.errorText = e.message
      }
    },
    async pollMap() {
      try {
        this.mapBuffer = await vacuumApi.mapRaw()
        this.mapError = ''
      } catch (e) {
        this.mapError = 'Karte (noch) nicht verfügbar'
      }
    },
    async pollRecords() {
      try {
        const r = await vacuumApi.cleanRecords(15)
        this.recordsSummary = r.summary
        this.records = r.records || []
      } catch (e) {
        // stiller Fehler — Liste ist nicht kritisch
      }
    },
    roomColor(room) { return segmentColor(room.segment_id) },
    isSelected(room) {
      return this.selectedSegments.includes(room.segment_id)
    },
    toggleRoom(room) {
      const i = this.selectedSegments.indexOf(room.segment_id)
      if (i === -1) this.selectedSegments.push(room.segment_id)
      else this.selectedSegments.splice(i, 1)
    },
    clearSelection() {
      this.selectedSegments = []
    },
    startSelection() {
      if (this.selectedSegments.length) this.dialog = true
    },
    addRoomFromMap() {
      // Dialog schließen und den nächsten Karten-Klick als „Raum hinzufügen" werten.
      this.dialog = false
      this.addingFromMap = true
      this.mapMode = 'select'
      this.snack = { show: true, msg: 'Tippe einen weiteren Raum auf der Karte an', color: 'info' }
    },
    removeRoom(segId) {
      const i = this.selectedSegments.indexOf(segId)
      if (i !== -1) this.selectedSegments.splice(i, 1)
      if (!this.selectedSegments.length) this.dialog = false
    },
    onMapSegment(segId) {
      const room = this.rooms.find(r => r.segment_id === segId)
      if (!room) return
      if (this.addingFromMap) {
        // Weiteren Raum zur bestehenden Auswahl hinzufügen und Dialog wieder öffnen.
        if (!this.selectedSegments.includes(segId)) this.selectedSegments.push(segId)
        this.addingFromMap = false
        this.dialog = true
      } else {
        // Direkter Klick auf der Karte: nur diesen Raum wählen und Reinigung konfigurieren.
        this.selectedSegments = [segId]
        this.dialog = true
      }
    },
    onStarted(rooms) {
      const names = rooms.map(r => r.name).join(', ')
      this.snack = { show: true, msg: `Reinigung gestartet: ${names}`, color: 'success' }
      this.clearSelection()
      this.pollStatus()
    },
    togglePinAndGo() {
      this.mapMode = this.mapMode === 'pin-and-go' ? 'select' : 'pin-and-go'
      this.placingDevice = null
      this.placingRoutine = null
    },

    // ----- Geräte auf der Karte -----
    startPlacing(device) {
      this.placingDevice = device
      this.placingRoutine = null
      this.deviceDialog = false
      this.mapMode = 'place-device'
      this.snack = { show: true, msg: `Tippe auf die Karte, um „${device.display_name}" zu platzieren`, color: 'info' }
    },
    async onPlaceDevice({ x, y }) {
      const device = this.placingDevice
      const routine = this.placingRoutine
      this.mapMode = 'select'
      this.placingDevice = null
      this.placingRoutine = null
      if (routine) {
        try {
          await smarthausApi.routineSetPosition(routine.id, x, y)
          this.snack = { show: true, msg: `Trigger „${routine.name}" platziert`, color: 'success' }
          this.pollDevices()
        } catch (e) {
          this.snack = { show: true, msg: 'Platzieren fehlgeschlagen: ' + e.message, color: 'error' }
        }
        return
      }
      if (!device) return
      try {
        await devicesApi.setPosition(device.id, x, y)
        this.snack = { show: true, msg: `${device.display_name} platziert`, color: 'success' }
        this.pollDevices()
      } catch (e) {
        this.snack = { show: true, msg: 'Platzieren fehlgeschlagen: ' + e.message, color: 'error' }
      }
    },

    // ----- Routinen-Trigger auf der Karte -----
    startPlacingRoutine(routine) {
      this.placingRoutine = routine
      this.placingDevice = null
      this.deviceDialog = false
      this.mapMode = 'place-device'
      this.snack = { show: true, msg: `Tippe auf die Karte, um den Trigger „${routine.name}" zu platzieren`, color: 'info' }
    },
    applyRoutine(updated) {
      const idx = this.routines.findIndex(r => r.id === updated.id)
      if (idx !== -1) this.$set(this.routines, idx, updated)
    },
    async toggleRoutine(routine) {
      try {
        const r = await smarthausApi.routineToggle(routine.id)
        if (r.routine) this.applyRoutine(r.routine)
        const active = r.routine ? r.routine.active : !routine.active
        this.snack = { show: true, msg: `Routine „${routine.name}" ${active ? 'aktiviert' : 'deaktiviert'}`, color: 'success' }
      } catch (e) {
        this.snack = { show: true, msg: 'Routine fehlgeschlagen: ' + e.message, color: 'error' }
      }
    },
    async setRoutineActive(routine, active) {
      try {
        const r = await smarthausApi.routineActive(routine.id, !!active)
        if (r.routine) this.applyRoutine(r.routine)
        this.snack = { show: true, msg: `Routine „${routine.name}" ${active ? 'aktiviert' : 'deaktiviert'}`, color: 'success' }
      } catch (e) {
        this.snack = { show: true, msg: 'Routine fehlgeschlagen: ' + e.message, color: 'error' }
      }
    },
    async removeRoutineFromMap(routine) {
      try {
        const r = await smarthausApi.routineClearPosition(routine.id)
        if (r.routine) this.applyRoutine(r.routine)
      } catch (e) {
        this.snack = { show: true, msg: 'Entfernen fehlgeschlagen: ' + e.message, color: 'error' }
      }
    },
    async removeDeviceFromMap(device) {
      try {
        await devicesApi.clearPosition(device.id)
        this.pollDevices()
      } catch (e) {
        this.snack = { show: true, msg: 'Entfernen fehlgeschlagen: ' + e.message, color: 'error' }
      }
    },
    toggleDevice(device) {
      this.setDeviceOn(device, !device.on)
    },
    async setDeviceOn(device, on) {
      try {
        const r = await devicesApi.setOn(device.id, on)
        // Antwort direkt einpflegen, damit der Marker ohne Poll-Delay umspringt.
        const idx = this.devices.findIndex(d => d.id === device.id)
        if (idx !== -1 && r.device) this.$set(this.devices, idx, r.device)
        this.snack = { show: true, msg: `${device.display_name} ${on ? 'an' : 'aus'}`, color: 'success' }
      } catch (e) {
        this.snack = { show: true, msg: 'Schalten fehlgeschlagen: ' + e.message, color: 'error' }
      }
    },
    async onPinAndGo({ x, y }) {
      try {
        await vacuumApi.goto(x, y)
        this.gotoTarget = { x, y }
        this.snack = { show: true, msg: 'Schnuffel fährt zum Ziel', color: 'success' }
        this.mapMode = 'select'
        this.pollStatus()
      } catch (e) {
        this.snack = { show: true, msg: 'Pin&Go fehlgeschlagen: ' + e.message, color: 'error' }
      }
    },
    async act(name) {
      this.busy[name] = true
      try {
        await vacuumApi[name]()
        this.snack = { show: true, msg: 'OK · ' + name, color: 'success' }
        this.pollStatus()
      } catch (e) {
        this.snack = { show: true, msg: 'Fehler: ' + e.message, color: 'error' }
      } finally {
        this.busy[name] = false
      }
    },
  },
}
</script>

<style scoped>
.vacuum-view { color: #fff; }
.room-tile {
  position: relative;
  cursor: pointer;
  transition: transform 120ms ease;
}
.room-tile:hover { transform: translateY(-2px); }
.room-selected {
  outline: 3px solid #fff;
  outline-offset: -3px;
}
.room-check {
  position: absolute;
  top: 4px;
  right: 4px;
  color: #fff;
  z-index: 1;
}
</style>
